import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Alert,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import LockIcon from '@mui/icons-material/Lock';
import { useDispatch } from 'react-redux';
import { SHOW_NOTIFICATION, SUBMIT_BUTTON_TEXT } from '../../../constants';
import { getUsers } from '../../../services/UserService';
import PaginationComponent from '../../../components/Pagination/Pagination';
import {
  getQuarterlyCasualLeaveAllocation,
  getQuarterlyCasualLeaveAdjustments,
  updateQuarterlyCasualLeaveAllocation,
} from '../../Services/QuarterlyCasualLeaveService';

const DEFAULT_ADJUSTMENT_PAGE_SIZE = 10;

const formatAdjustment = (value) => {
  if (value === 0) return '0';
  return value > 0 ? `+${value}` : `${value}`;
};

const statusLabel = (status) => {
  if (status === 'PROVISIONAL') return 'Expected (not spendable)';
  if (status === 'GRANTED') return 'Granted';
  if (status === 'FINALIZED') return 'Finalized';
  return status;
};

const temporalChipLabel = (position) => {
  if (position === 'FUTURE') return 'Future';
  if (position === 'CURRENT') return 'Current';
  if (position === 'PAST') return 'Past';
  return null;
};

const validateReallocationClientSide = (quarters, editedEffective) => {
  let pastDecrease = 0;
  let pastIncrease = 0;
  let currentIncrease = 0;
  let futureDecrease = 0;
  let futureIncrease = 0;

  for (const row of quarters || []) {
    const baseline = row.effectiveAllocationDays ?? 0;
    const proposed =
      editedEffective[row.quarter] !== undefined
        ? editedEffective[row.quarter]
        : baseline;
    if (proposed === '' || proposed == null || Number.isNaN(proposed)) {
      return `Effective allocation is required for Q${row.quarter}`;
    }
    const proposedNum = Number(proposed);
    const delta = proposedNum - baseline;
    const maxDays = row.maxAllocationDays ?? 0;

    if (row.temporalPosition === 'FUTURE' && proposedNum > maxDays) {
      return `Future quarter Q${row.quarter} cannot exceed Band 1 max of ${maxDays} days`;
    }

    if (row.temporalPosition === 'PAST') {
      if (delta > 0) pastIncrease += delta;
      else if (delta < 0) pastDecrease += -delta;
    } else if (row.temporalPosition === 'CURRENT') {
      if (delta > 0) currentIncrease += delta;
    } else if (row.temporalPosition === 'FUTURE') {
      if (delta > 0) futureIncrease += delta;
      else if (delta < 0) futureDecrease += -delta;
    }
  }

  if (pastIncrease > 0) {
    return 'Cannot increase past quarter allocations; transfer out only to current or future';
  }
  if (futureIncrease > pastDecrease) {
    return 'Future increases must come from past quarters (cannot move current → future)';
  }
  const availableForCurrent = pastDecrease + futureDecrease - futureIncrease;
  if (currentIncrease > availableForCurrent) {
    return `Current quarter increases must be funded from future/past quarters (available: ${availableForCurrent})`;
  }
  return null;
};

const QuarterlyCasualLeaveAllocationComponent = () => {
  const dispatch = useDispatch();
  const currentYear = new Date().getFullYear();
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [year, setYear] = useState(String(currentYear));
  const [allocationView, setAllocationView] = useState(null);
  const [editedEffective, setEditedEffective] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [adjustmentPage, setAdjustmentPage] = useState(0);
  const [adjustmentPageSize, setAdjustmentPageSize] = useState(
    DEFAULT_ADJUSTMENT_PAGE_SIZE,
  );
  const [adjustmentsLoading, setAdjustmentsLoading] = useState(false);

  const notify = (type, message) => {
    dispatch({
      type: SHOW_NOTIFICATION,
      data: { open: true, type, message },
    });
  };

  useEffect(() => {
    const loadEmployees = async () => {
      const response = await getUsers('');
      if (response.isError === false) {
        setEmployees(response.data.content || []);
      } else if (response.message) {
        notify(response.type, response.message);
      }
    };
    loadEmployees();
  }, []);

  const normalizeAllocationView = (response) => {
    const candidate = response?.data?.data ?? response?.data;
    if (candidate && Array.isArray(candidate.quarters)) {
      return candidate;
    }
    return null;
  };

  const normalizeAdjustmentsPage = (response) => {
    const candidate = response?.data?.data ?? response?.data;
    if (candidate && Array.isArray(candidate.content)) {
      return candidate;
    }
    return null;
  };

  const applyAllocationView = (nextView) => {
    setAllocationView(nextView);
    setEditedEffective({});
    setAdjustmentPage(nextView.adjustmentsPage ?? 0);
    setAdjustmentPageSize(
      nextView.adjustmentsPageSize ?? DEFAULT_ADJUSTMENT_PAGE_SIZE,
    );
  };

  const loadAllocation = async (
    page = adjustmentPage,
    size = adjustmentPageSize,
  ) => {
    if (!selectedEmployee?.employeeId) {
      notify('error', 'Please select an employee');
      return;
    }
    setLoading(true);
    const response = await getQuarterlyCasualLeaveAllocation(
      selectedEmployee.employeeId,
      year,
      page,
      size,
    );
    setLoading(false);
    if (response.isError === false) {
      const nextView = normalizeAllocationView(response);
      if (nextView) {
        applyAllocationView(nextView);
      } else {
        notify('error', 'Allocation data was incomplete. Please try again.');
      }
    } else {
      notify(response.type, response.message);
    }
  };

  const loadAdjustmentsPage = async (page, size) => {
    if (!selectedEmployee?.employeeId || !allocationView) return;
    setAdjustmentsLoading(true);
    const response = await getQuarterlyCasualLeaveAdjustments(
      selectedEmployee.employeeId,
      year,
      page,
      size,
    );
    setAdjustmentsLoading(false);
    if (response.isError === false) {
      const pageData = normalizeAdjustmentsPage(response);
      if (pageData) {
        setAllocationView((prev) =>
          prev
            ? {
                ...prev,
                recentAdjustments: pageData.content,
                adjustmentsPage: pageData.page,
                adjustmentsPageSize: pageData.size,
                adjustmentsTotalElements: pageData.totalElements,
                adjustmentsTotalPages: pageData.totalPages,
              }
            : prev,
        );
        setAdjustmentPage(pageData.page);
        setAdjustmentPageSize(pageData.size);
      } else {
        notify('error', 'Could not load adjustments page');
      }
    } else {
      notify(response.type, response.message);
    }
  };

  const handleEffectiveChange = (quarterRow, value) => {
    if (!quarterRow.editable) return;
    const sanitized = value.replace(/\D/g, '');
    if (sanitized === '') {
      setEditedEffective((prev) => ({ ...prev, [quarterRow.quarter]: '' }));
      return;
    }
    let next = parseInt(sanitized, 10);
    if (
      quarterRow.temporalPosition === 'FUTURE' &&
      quarterRow.maxAllocationDays != null
    ) {
      next = Math.min(next, quarterRow.maxAllocationDays);
    }
    setEditedEffective((prev) => ({
      ...prev,
      [quarterRow.quarter]: next,
    }));
  };

  const getEffectiveValue = (quarterRow) => {
    if (editedEffective[quarterRow.quarter] !== undefined) {
      return editedEffective[quarterRow.quarter];
    }
    return quarterRow.effectiveAllocationDays;
  };

  const getPreviewAdjustment = (quarterRow) => {
    const effective = Number(getEffectiveValue(quarterRow));
    if (Number.isNaN(effective)) return quarterRow.adminAdjustmentDays;
    return effective - (quarterRow.bandBaseDays ?? 0);
  };

  const buildChangedAllocations = () => {
    if (!allocationView?.quarters) return [];
    return allocationView.quarters
      .filter((q) => q.editable)
      .filter((q) => {
        const edited = editedEffective[q.quarter];
        return edited !== undefined && edited !== '' && edited !== q.effectiveAllocationDays;
      })
      .map((q) => ({
        quarter: q.quarter,
        effectiveAllocation: editedEffective[q.quarter],
      }));
  };

  const handleSaveClick = () => {
    const changes = buildChangedAllocations();
    if (changes.length === 0) {
      notify('info', 'No allocation changes to save');
      return;
    }
    const validationError = validateReallocationClientSide(
      allocationView.quarters,
      editedEffective,
    );
    if (validationError) {
      notify('error', validationError);
      return;
    }
    setReason('');
    setReasonDialogOpen(true);
  };

  const handleConfirmSave = async () => {
    if (!reason.trim()) {
      notify('error', 'Reason is required');
      return;
    }
    const validationError = validateReallocationClientSide(
      allocationView.quarters,
      editedEffective,
    );
    if (validationError) {
      notify('error', validationError);
      return;
    }
    const allocations = buildChangedAllocations();
    setSaving(true);
    const response = await updateQuarterlyCasualLeaveAllocation(
      selectedEmployee.employeeId,
      year,
      { allocations, reason: reason.trim() },
    );
    setSaving(false);
    if (response.isError === false) {
      const nextView = normalizeAllocationView(response);
      if (nextView) {
        applyAllocationView(nextView);
        setReasonDialogOpen(false);
        notify(response.type, 'Quarterly Casual Leave allocation updated');
      } else {
        setReasonDialogOpen(false);
        notify(
          'error',
          'Update may have succeeded but allocation data was incomplete. Reloading…',
        );
        await loadAllocation(0, adjustmentPageSize);
      }
    } else {
      notify(response.type, response.message || 'Failed to update allocation');
    }
  };

  const handleAdjustmentPageChange = (_, uiPage) => {
    const zeroBased = uiPage - 1;
    setAdjustmentPage(zeroBased);
    loadAdjustmentsPage(zeroBased, adjustmentPageSize);
  };

  const handleAdjustmentPageSizeChange = (event) => {
    const nextSize = Number(event.target.value);
    setAdjustmentPageSize(nextSize);
    setAdjustmentPage(0);
    loadAdjustmentsPage(0, nextSize);
  };

  const yearOptions = [currentYear - 1, currentYear, currentYear + 1].map(
    String,
  );

  const adjustmentsTotalPages = Math.max(
    1,
    allocationView?.adjustmentsTotalPages || 1,
  );
  const hasAdjustments =
    (allocationView?.adjustmentsTotalElements ?? 0) > 0 ||
    (allocationView?.recentAdjustments?.length ?? 0) > 0;

  return (
    <Box className="main-content-overlay">
      <div className="main-content-div">
        <Typography variant="h6" sx={{ mb: 2 }}>
          Quarterly Casual Leave Allocation
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Reallocate quarterly Casual Leave from future (or past) quarters into
          the current quarter. Future quarters cannot exceed Band 1 max;
          carry-forward and other ledger movements sit on top of this base
          allocation.
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={5}>
            <Autocomplete
              options={employees.map((emp) => ({
                employeeId: emp.id,
                employeeName: `${emp.firstName} ${emp.lastName} (${emp.employeeCode})`,
              }))}
              getOptionLabel={(option) => option.employeeName || ''}
              isOptionEqualToValue={(option, value) =>
                option.employeeId === value.employeeId
              }
              value={selectedEmployee}
              onChange={(_, value) => {
                setSelectedEmployee(value);
                setAllocationView(null);
                setEditedEffective({});
                setAdjustmentPage(0);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Employee Name / ID" size="small" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Year</InputLabel>
              <Select
                label="Year"
                value={year}
                onChange={(e) => {
                  setYear(e.target.value);
                  setAllocationView(null);
                  setEditedEffective({});
                  setAdjustmentPage(0);
                }}
              >
                {yearOptions.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <LoadingButton
              variant="contained"
              loading={loading}
              onClick={() => loadAllocation(0, adjustmentPageSize)}
              sx={{ height: '40px' }}
            >
              Load
            </LoadingButton>
          </Grid>
        </Grid>

        {allocationView && (
          <>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Annual Potential Pool
                  </Typography>
                  <Typography variant="h6">
                    {allocationView.annualPotentialPool}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Allocated Across Quarters
                  </Typography>
                  <Typography variant="h6">
                    {allocationView.allocatedAcrossQuarters}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Transferable to Current
                  </Typography>
                  <Typography variant="h6">
                    {allocationView.remainingReallocationCapacity}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Year Carry Forward (separate)
                  </Typography>
                  <Typography variant="h6">
                    {allocationView.totalYearCfAppliedDays}
                  </Typography>
                </Grid>
              </Grid>
              <Alert severity="info" sx={{ mt: 2 }}>
                Reallocation moves leave from future → current (or past →
                current/future). Future quarters are hard-capped at Band 1 max
                and cannot be increased from the current quarter. Year
                carry-forward and other ledger credits apply on top of this
                quarterly base after grant.
              </Alert>
            </Paper>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Quarter</TableCell>
                    <TableCell align="right">Base</TableCell>
                    <TableCell align="right">Band 1 Max</TableCell>
                    <TableCell align="right">Admin Adjustment</TableCell>
                    <TableCell align="right">Effective</TableCell>
                    <TableCell align="right">Accumulated</TableCell>
                    <TableCell align="right">CF</TableCell>
                    <TableCell align="right">Used</TableCell>
                    <TableCell align="right">Deficit</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(allocationView.quarters || []).map((row) => {
                    const isFuture = row.temporalPosition === 'FUTURE';
                    const maxDays = row.maxAllocationDays;
                    const temporalLabel = temporalChipLabel(row.temporalPosition);
                    return (
                      <TableRow key={row.quarter}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            Q{row.quarter}
                            {temporalLabel && (
                              <Chip label={temporalLabel} size="small" variant="outlined" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{row.bandBaseDays}</TableCell>
                        <TableCell align="right">{maxDays ?? '-'}</TableCell>
                        <TableCell align="right">
                          {formatAdjustment(getPreviewAdjustment(row))}
                        </TableCell>
                        <TableCell align="right">
                          {row.editable ? (
                            <Box>
                              <TextField
                                size="small"
                                type="number"
                                inputProps={{
                                  min: 0,
                                  max: isFuture && maxDays != null ? maxDays : undefined,
                                  style: { textAlign: 'right' },
                                }}
                                value={getEffectiveValue(row)}
                                onChange={(e) =>
                                  handleEffectiveChange(row, e.target.value)
                                }
                                error={
                                  isFuture &&
                                  maxDays != null &&
                                  Number(getEffectiveValue(row)) > maxDays
                                }
                                sx={{ width: 88 }}
                              />
                              {isFuture && maxDays != null && (
                                <FormHelperText sx={{ m: 0, textAlign: 'right' }}>
                                  Max {maxDays}
                                </FormHelperText>
                              )}
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: 0.5,
                              }}
                            >
                              {row.effectiveAllocationDays}
                              <LockIcon fontSize="inherit" color="disabled" />
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="right">{row.accumulatedInDays}</TableCell>
                        <TableCell align="right">{row.yearCfAppliedDays}</TableCell>
                        <TableCell align="right">{row.usageDays}</TableCell>
                        <TableCell align="right">{row.deficitDays}</TableCell>
                        <TableCell>
                          <Chip
                            label={statusLabel(row.status)}
                            size="small"
                            color={
                              row.status === 'FINALIZED'
                                ? 'default'
                                : row.status === 'GRANTED'
                                  ? 'success'
                                  : 'warning'
                            }
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <LoadingButton
                variant="contained"
                onClick={handleSaveClick}
                disabled={buildChangedAllocations().length === 0}
              >
                Save Changes
              </LoadingButton>
            </Box>

            {hasAdjustments && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Recent Admin Adjustments
                </Typography>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ maxHeight: 360, overflow: 'auto' }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Quarter</TableCell>
                        <TableCell align="right">Adjustment Days</TableCell>
                        <TableCell>Admin</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(allocationView.recentAdjustments || []).map((entry) => (
                        <TableRow key={entry.ledgerId}>
                          <TableCell>
                            {entry.createdAt
                              ? new Date(entry.createdAt).toLocaleString()
                              : '-'}
                          </TableCell>
                          <TableCell>Q{entry.quarter}</TableCell>
                          <TableCell align="right">
                            {entry.adjustmentDays}
                          </TableCell>
                          <TableCell>{entry.adminUserId || '-'}</TableCell>
                          <TableCell>{entry.reason || '-'}</TableCell>
                        </TableRow>
                      ))}
                      {adjustmentsLoading && (
                        <TableRow>
                          <TableCell colSpan={5}>Loading…</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box className="page-holder" sx={{ mt: 2 }}>
                  <PaginationComponent
                    count={adjustmentsTotalPages}
                    page={(allocationView.adjustmentsPage ?? 0) + 1}
                    pageChange={handleAdjustmentPageChange}
                    pageSize={adjustmentPageSize}
                    handlePageSizeChange={handleAdjustmentPageSizeChange}
                    paginationTitle="Rows per page"
                  />
                </Box>
              </Box>
            )}
          </>
        )}

        <Dialog
          open={reasonDialogOpen}
          onClose={() => setReasonDialogOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Confirm Allocation Change</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Provide a reason for this admin adjustment. Changes are applied
              atomically across all modified quarters.
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReasonDialogOpen(false)}>Cancel</Button>
            <LoadingButton
              variant="contained"
              loading={saving}
              onClick={handleConfirmSave}
            >
              {SUBMIT_BUTTON_TEXT}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </div>
    </Box>
  );
};

export default QuarterlyCasualLeaveAllocationComponent;
